import { calendarDate, formatAppDate, formatAppDateTime } from '../utility/dateLocale';
import { InvoiceMonthFilter } from '../components/InvoiceMonthFilter';
import { matchesPageMonth } from '../utility/pageMonthFilter';
import React, { useMemo, useState, useEffect, useRef } from 'react';
import {
  Modal,
  Button,
  TextInput,
  Textarea,
  Select,
  Group,
  Title,
  Text,
  SegmentedControl,
  Card,
  Image,
  ActionIcon,
  NumberInput,
  Menu,
  ScrollArea,
  Table,
  Stack,
  Badge,
  useMantineTheme,
  Paper,
  Box,
  Switch,
  Divider,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { type MRT_ColumnDef, type MRT_Row } from 'mantine-react-table';
import { ExportableMantineReactTable, useExportableTable } from '../utility/useExportableTable';
import { enableGlobalSearchOnColumns, filterSelectByLabel } from '../utility/mrtTableSearch';
import { ImportConfirmModal } from '../components/ImportConfirmModal';
import { TableExcelImportModal } from '../components/TableExcelImportModal';
import { classifyImportRows } from '../utility/excelDeletedColumn';
import { executeImportUpsert } from '../utility/executeImportUpsert';
import {
  formKeysPresentInRow,
  mergeDefinedFields,
  readRowCell,
} from '../utility/excelImportHelpers';
import { readExcelFile, splitImportRowsById } from '../utility/tableExcelExport';
import {
  IconFlag,
  IconRefresh,
  IconTrophy,
  IconFilter,
  IconPaperclip,
  IconTrash,
  IconPhoto,
  IconFile,
  IconPlus,
  IconTarget,
  IconSettings,
  IconSend,
  IconSearch,
} from '@tabler/icons-react';
import { useDropzone, type FileRejection } from 'react-dropzone';
import { DndContext, DragOverlay, pointerWithin, useDraggable, useDroppable } from '@dnd-kit/core';
import { Dots, FileText } from 'tabler-icons-react';
import './KanbanColumn.css';
import Environment from '../utility/environment';
import { useAuth } from '../contexts/AuthProvider';
import { parsePhotoUrl } from '../utility/parsers';
import { useNotifications } from '../contexts/NotificationProvider';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { buildMrtLocalization } from '../i18n/mrtLocale';
import { AttachmentActionButtons } from '../components/AttachmentActionButtons';
import { useAttachmentPreview } from '../components/AttachmentPreviewModal';
import {
  attachmentSourceFromFields,
  downloadAttachment,
  type AttachmentFileSource,
} from '../utility/openRemoteFile';
import { useMrtTableContainerProps, useModalScrollAreaHeight, usePageTableShellStyle } from '../utility/mobilePageLayout';
import { MentionTextarea, formatMentionText, type MentionUser } from '../components/MentionTextarea';
import { buildPageUrl, readPageFilterParam } from '../utility/pageNavigationFilters';

const TASK_TYPE_I18N_KEY: Record<string, string> = {
  tarefa: 'tasks.typeOptions.task',
  'demonstração': 'tasks.typeOptions.demonstration',
  mensagem: 'tasks.typeOptions.message',
  email: 'tasks.typeOptions.email',
  'reunião presencial': 'tasks.typeOptions.meetingInPerson',
  'reunião online': 'tasks.typeOptions.meetingOnline',
  financeiro: 'tasks.typeOptions.financial',
  outros: 'tasks.typeOptions.other',
};

type TaskSortField = 'created_at' | 'deadline' | 'priority' | 'points';

type TaskStage = {
  id: string;
  enterprise_id: string;
  name: string;
  position: number;
  is_done: boolean;
};

type TaskComment = {
  id: string;
  task_id: string;
  user_id: string;
  user_name: string;
  user_image: string;
  body: string;
  created_at: string;
};

type EnterpriseTaskSettings = {
  enterprise_id: string;
  default_sort: TaskSortField;
  stages: TaskStage[];
};

const SPRINT_STATUS_I18N_KEY: Record<SprintStatus, string> = {
  planned: 'tasks.sprints.statusPlanned',
  active: 'tasks.sprints.statusActive',
  completed: 'tasks.sprints.statusCompleted',
};

const CRITICALITY_I18N_KEY: Record<string, string> = {
  completed: 'tasks.criticality.completed',
  overdue: 'tasks.criticality.overdue',
  within24h: 'tasks.criticality.within24h',
  within1week: 'tasks.criticality.within1week',
  over1week: 'tasks.criticality.over1week',
  all: 'tasks.criticality.all',
};

const FILTER_CRITICALITY = 'criticality';
const FILTER_RESPONSIBLE = 'responsible';
const FILTER_LINKED = 'linked';
const FILTER_ALL = 'all';

const STORY_POINT_OPTIONS = ['1', '2', '3', '5', '8', '13'];

type SprintStatus = 'planned' | 'active' | 'completed';

type Sprint = {
  id: string;
  name: string;
  enterprise_id: string;
  start_date: string;
  end_date: string;
  status: SprintStatus;
  goal?: string | null;
};

type SprintMetrics = {
  sprint_id: string;
  total_tasks: number;
  done_tasks: number;
  committed_points: number;
  completed_points: number;
  estimated_minutes: number;
  completed_minutes: number;
  days_remaining: number;
  completion_rate: number;
};

type SprintView = 'backlog' | string;


// --------------------------------------------------
// Tipos de Dados para Task
// --------------------------------------------------
type TaskAttachment = {
  id: string;
  file_url?: string;
  file_name: string;
  size: number;
  file?: File | string;
  upload_date?: string;
};

type Task = {
  id: number;
  uuid: string;
  name: string;
  responsible_id: string;
  responsible_name: string;
  responsible_image: string;
  created_by_id: string;
  created_by_name: string;
  created_by_image: string;
  created_at: string;
  contact_id: string;
  linked: string;
  taskType: string;
  dueDate: string;
  description: string;
  state: string;
  stage_id: string;
  stage_name: string;
  stage_is_done: boolean;
  priority: number;
  sprint_id: string | null;
  story_points: number | null;
  estimated_minutes: number | null;
  files: TaskAttachment[];
  comment_search_text: string;
  invoice_id?: string | null;
  deletedAt?: string | null;
};

function taskMatchesSearch(task: Task, query: string): boolean {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) {
    return true;
  }

  const haystack = [task.name, task.description, task.comment_search_text]
    .join(' ')
    .toLowerCase();

  return normalizedQuery
    .split(/\s+/)
    .filter(Boolean)
    .every((term) => haystack.includes(term));
}

type taskForm = Omit<Task, 'id' | 'uuid' | 'created_at' | 'stage_name' | 'stage_is_done' | 'comment_search_text'>;

const initialtaskForm: taskForm = {
  name: '',
  responsible_id: '',
  responsible_name: '',
  responsible_image: 'https://cdn-icons-png.flaticon.com/512/219/219990.png',
  created_by_id: '',
  created_by_name: '',
  created_by_image: '',
  contact_id: '',
  linked: '',
  taskType: 'tarefa',
  dueDate: '',
  description: '',
  state: 'To do',
  stage_id: '',
  priority: 0,
  sprint_id: null,
  story_points: null,
  estimated_minutes: null,
  files: [],
};

function parseApiErrorDetail(detail: unknown, fallback: string): string {
  if (typeof detail === 'string') {
    return detail;
  }
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (typeof item === 'string') {
          return item;
        }
        if (item && typeof item === 'object' && 'msg' in item) {
          const loc = 'loc' in item && Array.isArray(item.loc) ? item.loc.join('.') : '';
          return loc ? `${loc}: ${String(item.msg)}` : String(item.msg);
        }
        return JSON.stringify(item);
      })
      .join('; ');
  }
  return fallback;
}

const stageIcons: Record<string, React.ReactNode> = {
  'To do': <IconFlag size={18} />,
  'Doing': <IconRefresh size={18} />,
  'Done': <IconTrophy size={18} />,
};

// --------------------------------------------------
// Funções para definir cor de borda e criticidade
// --------------------------------------------------
function getBorderColor(task: Task): string {
  if (task.stage_is_done || task.state === 'Done') {
    return '#86bf88';
  }
  if (!task.dueDate) {
    return '#b3ebf2';
  }
  const now = new Date();
  const dueDate = new Date(task.dueDate);
  const diff = dueDate.getTime() - now.getTime();
  if (diff < 0) {
    return '#fc7e7e'; // pastel red para atrasado
  } else if (diff <= 24 * 60 * 60 * 1000) {
    return '#f1c40f'; // pastel yellow para ≤ 24h
  } else if (diff <= 7 * 24 * 60 * 60 * 1000) {
    return '#86bf88'; // pastel green para ≤ 1 semana
  } else {
    return '#b3ebf2'; // pastel blue para > 1 semana
  }
}

function getCriticalityScore(task: Task): number {
  if (task.stage_is_done || task.state === 'Done') {
    return Number.MAX_SAFE_INTEGER;
  }
  if (!task.dueDate) {
    return 4;
  }
  const now = new Date();
  const dueDate = new Date(task.dueDate);
  const diff = dueDate.getTime() - now.getTime();
  if (diff < 0) return 0; // atrasado: prioridade máxima
  else if (diff <= 24 * 60 * 60 * 1000) return 1;
  else if (diff <= 7 * 24 * 60 * 60 * 1000) return 2;
  else return 3;
}

// Função para categorizar a criticidade
function getCriticityCategory(task: Task): string {
  if (task.stage_is_done || task.state === 'Done') return 'completed';
  if (!task.dueDate) return 'over1week';
  const now = new Date();
  const dueDate = new Date(task.dueDate);
  const diff = dueDate.getTime() - now.getTime();
  if (diff < 0) return 'overdue';
  if (diff <= 24 * 60 * 60 * 1000) return 'within24h';
  if (diff <= 7 * 24 * 60 * 60 * 1000) return 'within1week';
  return 'over1week';
}



// --------------------------------------------------
// Componente: TaskCard (estilo Road Map)
// --------------------------------------------------
type TaskCardProps = {
  task: Task;
  onClick: () => void;
  icon: React.ReactNode;
};

const TaskCard = ({ task, onClick, icon, taskTypeLabel }: TaskCardProps & { taskTypeLabel: string }) => {
  const { t } = useTranslation();
  const borderColor = getBorderColor(task);
  return (
    <Card
      shadow="sm"
      radius="sm"
      p="xs"
      style={{
        width: 280,
        margin: 'auto',
        borderLeft: `6px solid ${borderColor}`,
        backgroundColor: '#fff',
        cursor: 'pointer',
      }}
      onClick={onClick}
    >
      {/* Top Bar */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          minWidth: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
          <div style={{ color: borderColor, marginRight: 6, display: 'flex', alignItems: 'center' }}>
            {icon}
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              textAlign: 'left',
              minWidth: 0,
            }}
          >
            <Text
              weight={700}
              size="xs"
              style={{
                fontSize: '10px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {t('tasks.card.id')}: {task.id}
            </Text>
            <Text
              weight={700}
              size="sm"
              style={{
                fontSize: '14px',
                lineHeight: 1.25,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {task.name}
            </Text>
          </div>
        </div>
        <ActionIcon
          variant="light"
          onPointerDown={(e: React.PointerEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          onMouseDown={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          style={{ backgroundColor: borderColor, color: '#fff', zIndex: 10 }}
        >
          <Dots size={16} />
        </ActionIcon>
      </div>

      <Group spacing={6} align="center" mt="xs">
        {task.responsible_image && (
          <Image
            src={parsePhotoUrl(task.responsible_image)}
            alt={task.responsible_id}
            width={22}
            height={22}
            radius="xl"
          />
        )}
        <Text weight={500} size="xs" color="dimmed">
          {task.responsible_name}
        </Text>
      </Group>

      <div style={{ marginTop: 8 }}>
        <Group position="apart" spacing="xs">
          <Text size="xs" color="dimmed">
            {t('tasks.card.oppClient')}
          </Text>
          <Text size="xs">{task.linked}</Text>
        </Group>
        <Group position="apart" spacing="xs">
          <Text size="xs" color="dimmed">
            {t('tasks.card.type')}
          </Text>
          <Text size="xs">{taskTypeLabel}</Text>
        </Group>
        <Group position="apart" spacing="xs">
          <Text size="xs" color="dimmed">
            {t('tasks.card.dueDate')}
          </Text>
          <Text size="xs">{formatAppDate(task.dueDate)}</Text>
        </Group>
        {task.story_points != null && task.story_points > 0 && (
          <Group position="apart" spacing="xs">
            <Text size="xs" color="dimmed">
              {t('tasks.sprints.form.storyPoints')}
            </Text>
            <Badge size="xs" color="teal" variant="light">
              {t('tasks.sprints.pointsBadge', { points: task.story_points })}
            </Badge>
          </Group>
        )}
        {task.files.length > 0 && (
          <Group position="apart" spacing="xs">
            <Text size="xs" color="dimmed">
              {t('tasks.card.attachments')}
            </Text>
            <Badge size="xs" leftSection={<IconPaperclip size={10} />}>
              {task.files.length}
            </Badge>
          </Group>
        )}
        <Group position="apart" spacing="xs">
          <Text size="xs" color="dimmed">
            {t('tasks.card.description')}
          </Text>
          <Text size="xs">
            {task.description.length > 18
              ? task.description.slice(0, 18) + '...'
              : task.description}
          </Text>
        </Group>
      </div>
    </Card>
  );
};

// --------------------------------------------------
// Componente: DraggableTaskCard (wrapper para DnD)
// --------------------------------------------------
type DraggableTaskCardProps = {
  task: Task;
  onClick: () => void;
};

function DraggableTaskCard({ task, onClick }: DraggableTaskCardProps) {
  const { t } = useTranslation();
  const taskTypeLabel = t(TASK_TYPE_I18N_KEY[task.taskType] ?? task.taskType);
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: `task-${task.id}`,
  });
  const style = {
    transform: transform ? `translate(${transform.x}px, ${transform.y}px)` : undefined,
    marginBottom: '8px',
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <TaskCard
        task={task}
        onClick={onClick}
        icon={stageIcons[task.state] || <IconFlag size={18} />}
        taskTypeLabel={taskTypeLabel}
      />
    </div>
  );
}

function sortTasks(tasks: Task[], sortBy: TaskSortField): Task[] {
  const sorted = [...tasks];
  sorted.sort((a, b) => {
    if (sortBy === 'deadline') {
      if (!a.dueDate && !b.dueDate) return 0;
      if (!a.dueDate) return 1;
      if (!b.dueDate) return -1;
      return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
    }
    if (sortBy === 'priority') {
      return b.priority - a.priority || new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    }
    if (sortBy === 'points') {
      const pointsA = a.story_points ?? -1;
      const pointsB = b.story_points ?? -1;
      return pointsB - pointsA || new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    }
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });
  return sorted;
}

// --------------------------------------------------
// Componente: KanbanColumn (coluna para cada estágio)
// --------------------------------------------------
type KanbanColumnProps = {
  stage: TaskStage;
  tasks: Task[];
  sortBy: TaskSortField;
  onCardClick: (task: Task) => void;
};

type Options = {
  value: string;
  label: string;
}

interface Contacts extends Options {
  clients: string[]
}

interface Opportunity extends Options {
  client_ids: string[]
}

function KanbanColumn({ stage, tasks, sortBy, onCardClick }: KanbanColumnProps) {
  const { t, i18n } = useTranslation();
  const { isOver, setNodeRef } = useDroppable({ id: stage.id });

  // Estados para os filtros combinados
  const [activeFilters, setActiveFilters] = useState<{ [key: string]: string }>({});

  const handleClearFilters = () => {
    setActiveFilters({});
  };


  const criticityOptions = useMemo(
    () =>
      ['all', 'overdue', 'within24h', 'within1week', 'over1week', 'completed'].map((value) => ({
        value,
        label: t(CRITICALITY_I18N_KEY[value]),
      })),
    [t, i18n.language],
  );

  const responsibleOptionsForColumn = useMemo(() => {
    const names = Array.from(new Set(tasks.map((act) => act.responsible_name)));
    return [
      { value: FILTER_ALL, label: t('tasks.criticality.all') },
      ...names.map((name) => ({ value: name, label: name })),
    ];
  }, [tasks, t, i18n.language]);

  const linkedOptionsForColumn = useMemo(() => {
    const links = Array.from(new Set(tasks.map((act) => act.linked)));
    return [
      { value: FILTER_ALL, label: t('tasks.criticality.all') },
      ...links.map((link) => ({ value: link, label: link })),
    ];
  }, [tasks, t, i18n.language]);

  const sortedTasks = useMemo(() => {
    const filtered = tasks.filter((task) => {
      if (activeFilters[FILTER_CRITICALITY] && activeFilters[FILTER_CRITICALITY] !== FILTER_ALL) {
        if (getCriticityCategory(task) !== activeFilters[FILTER_CRITICALITY]) return false;
      }
      if (activeFilters[FILTER_RESPONSIBLE] && activeFilters[FILTER_RESPONSIBLE] !== FILTER_ALL) {
        if (task.responsible_name !== activeFilters[FILTER_RESPONSIBLE]) return false;
      }
      if (activeFilters[FILTER_LINKED] && activeFilters[FILTER_LINKED] !== FILTER_ALL) {
        if (task.linked !== activeFilters[FILTER_LINKED]) return false;
      }
      return true;
    });
    if (sortBy === 'deadline') {
      return [...filtered].sort((a, b) => {
        const scoreA = getCriticalityScore(a);
        const scoreB = getCriticalityScore(b);
        if (scoreA !== scoreB) return scoreA - scoreB;
        if (!a.dueDate && !b.dueDate) return 0;
        if (!a.dueDate) return 1;
        if (!b.dueDate) return -1;
        return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
      });
    }
    return sortTasks(filtered, sortBy);
  }, [tasks, activeFilters, sortBy]);

  const droppableStyle: React.CSSProperties = {
    backgroundColor: isOver ? '#fafafa' : 'rgb(239,239,239)',
    padding: '16px',
    borderTop: '3px solid #888',
    borderRadius: '8px',
    flex: '0 0 auto',
    maxWidth: '350px',
    minWidth: '300px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
    margin: '0 8px',
    display: 'flex',
    flexDirection: 'column',
  };

  return (
    <div ref={setNodeRef} style={droppableStyle}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #eee',
          paddingBottom: '16px',
        }}
      >
        <Title order={5} style={{ textAlign: 'left', margin: 0, fontWeight: 600 }}>
          {stage.name} ({tasks.length} {t('tasks.kanban.cards')})
        </Title>

        {/* Dropdown aninhado com Menu – submenus acionados em hover */}
        <Menu shadow="md" width={220}>
          <Menu.Target>
            <ActionIcon
              variant="light"
              color={
                (activeFilters[FILTER_CRITICALITY] && activeFilters[FILTER_CRITICALITY] !== FILTER_ALL) ||
                  (activeFilters[FILTER_RESPONSIBLE] && activeFilters[FILTER_RESPONSIBLE] !== FILTER_ALL) ||
                  (activeFilters[FILTER_LINKED] && activeFilters[FILTER_LINKED] !== FILTER_ALL)
                  ? 'blue'
                  : 'gray'
              }
            >
              <IconFilter size={18} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Label>{t('tasks.kanban.filters')}</Menu.Label>
            <Menu.Item>
              <Menu trigger="hover" position="right" transitionProps={{ duration: 0 }}>
                <Menu.Target>
                  <Text style={{ cursor: 'pointer' }}>{t('tasks.kanban.criticality')}</Text>
                </Menu.Target>
                <Menu.Dropdown>
                  {criticityOptions.map((option) => (
                    <Menu.Item
                      key={option.value}
                      onClick={() => {
                        if (option.value === FILTER_ALL) {
                          setActiveFilters((prev) => {
                            const updated = { ...prev };
                            delete updated[FILTER_CRITICALITY];
                            return updated;
                          });
                        } else {
                          setActiveFilters((prev) => ({ ...prev, [FILTER_CRITICALITY]: option.value }));
                        }
                      }}
                      sx={{
                        backgroundColor:
                          activeFilters[FILTER_CRITICALITY] === option.value ? '#30715B' : undefined,
                        color:
                          activeFilters[FILTER_CRITICALITY] === option.value ? '#fff' : undefined,
                      }}
                    >
                      {option.label}
                    </Menu.Item>
                  ))}
                </Menu.Dropdown>
              </Menu>
            </Menu.Item>
            <Menu.Item>
              <Menu trigger="hover" position="right" transitionProps={{ duration: 0 }}>
                <Menu.Target>
                  <Text style={{ cursor: 'pointer' }}>{t('tasks.kanban.responsible')}</Text>
                </Menu.Target>
                <Menu.Dropdown>
                  {responsibleOptionsForColumn.map((option) => (
                    <Menu.Item
                      key={option.value}
                      onClick={() => {
                        if (option.value === FILTER_ALL) {
                          setActiveFilters((prev) => {
                            const updated = { ...prev };
                            delete updated[FILTER_RESPONSIBLE];
                            return updated;
                          });
                        } else {
                          setActiveFilters((prev) => ({ ...prev, [FILTER_RESPONSIBLE]: option.value }));
                        }
                      }}
                      sx={{
                        backgroundColor:
                          activeFilters[FILTER_RESPONSIBLE] === option.value ? '#30715B' : undefined,
                        color:
                          activeFilters[FILTER_RESPONSIBLE] === option.value ? '#fff' : undefined,
                      }}
                    >
                      {option.label}
                    </Menu.Item>
                  ))}
                </Menu.Dropdown>
              </Menu>
            </Menu.Item>
            <Menu.Item>
              <Menu trigger="hover" position="right" transitionProps={{ duration: 0 }}>
                <Menu.Target>
                  <Text style={{ cursor: 'pointer' }}>{t('tasks.kanban.oppClient')}</Text>
                </Menu.Target>
                <Menu.Dropdown>
                  {linkedOptionsForColumn.map((option) => (
                    <Menu.Item
                      key={option.value}
                      onClick={() => {
                        if (option.value === FILTER_ALL) {
                          setActiveFilters((prev) => {
                            const updated = { ...prev };
                            delete updated[FILTER_LINKED];
                            return updated;
                          });
                        } else {
                          setActiveFilters((prev) => ({ ...prev, [FILTER_LINKED]: option.value }));
                        }
                      }}
                      sx={{
                        backgroundColor:
                          activeFilters[FILTER_LINKED] === option.value ? '#30715B' : undefined,
                        color:
                          activeFilters[FILTER_LINKED] === option.value ? '#fff' : undefined,
                      }}
                    >
                      {option.label}
                    </Menu.Item>
                  ))}
                </Menu.Dropdown>
              </Menu>
            </Menu.Item>
            <Menu.Item color="red" onClick={handleClearFilters}>
              {t('tasks.kanban.clearAll')}
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </div>

      <div className="cardsContainer">
        {sortedTasks.map((task) => (
          <DraggableTaskCard
            key={task.id}
            task={task}
            onClick={() => onCardClick(task)}
          />
        ))}
      </div>
    </div>
  );
}

type SprintDropZoneProps = {
  sprint: Sprint;
};

function SprintDropZone({ sprint }: SprintDropZoneProps) {
  const { t } = useTranslation();
  const { isOver, setNodeRef } = useDroppable({ id: `sprint-${sprint.id}` });
  const statusColor =
    sprint.status === 'active' ? '#30715B' : sprint.status === 'planned' ? '#228be6' : '#868e96';

  return (
    <Paper
      ref={setNodeRef}
      withBorder
      p="xs"
      radius="md"
      style={{
        minWidth: 140,
        minHeight: 56,
        borderColor: isOver ? '#30715B' : statusColor,
        backgroundColor: isOver ? 'rgba(48,113,91,0.08)' : '#fff',
      }}
    >
      <Group spacing={6} noWrap>
        <IconTarget size={16} color={statusColor} />
        <div style={{ minWidth: 0 }}>
          <Text size="xs" weight={600} lineClamp={1}>
            {sprint.name}
          </Text>
          <Text size="xs" color="dimmed">
            {t(SPRINT_STATUS_I18N_KEY[sprint.status])}
          </Text>
        </div>
      </Group>
    </Paper>
  );
}

// --------------------------------------------------
// Página de Tasks (com toggle entre Tabela e Kanban)
// --------------------------------------------------
export default function TasksPage() {
  const [selectedMonth, setSelectedMonth] = useState('all');
  const theme = useMantineTheme();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const taskIdFromUrl = readPageFilterParam(location.search, 'taskId');
  const openedTaskFromUrl = useRef(false);
  const tableShellStyle = usePageTableShellStyle();
  const mrtTableContainerProps = useMrtTableContainerProps();
  const modalScrollAreaHeight = useModalScrollAreaHeight();
  const [taskSearchQuery, setTaskSearchQuery] = useState('');
  const [tasks, setTasks] = useState<Task[]>([]);
  const [sprints, setSprints] = useState<Sprint[]>([]);
  const [sprintsLoaded, setSprintsLoaded] = useState(false);
  const [sprintView, setSprintView] = useState<SprintView>('backlog');
  const [sprintMetrics, setSprintMetrics] = useState<SprintMetrics | null>(null);
  const [sprintModalOpened, setSprintModalOpened] = useState(false);
  const [savingSprint, setSavingSprint] = useState(false);
  const [bulkSprintId, setBulkSprintId] = useState<string | null>(null);
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [newSprintForm, setNewSprintForm] = useState({
    name: '',
    goal: '',
    start_date: '',
    end_date: '',
    status: 'planned' as SprintStatus,
  });
  const [modalOpened, setModalOpened] = useState(false);
  const [editingTask, seteditingTask] = useState<Task | null>(null);
  const [taskForm, settaskForm] = useState<taskForm>(initialtaskForm);
  const [errorMessage, setErrorMessage] = useState('');
  const [attachmentErrors, setAttachmentErrors] = useState<string[]>([]);
  const [autoImportFile, setAutoImportFile] = useState<File | null>(null);
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const [importNotification, setImportNotification] = useState<{ message: string; color: 'green' | 'red' } | null>(null);
  const [importModalOpened, setImportModalOpened] = useState(false);
  const [importConfirm, setImportConfirm] = useState<{
    newCount: number;
    updateCount: number;
    deleteCount: number;
    run: () => Promise<void>;
  } | null>(null);
  const [saving, setSaving] = useState(false);
  const [downloadingAttachmentId, setDownloadingAttachmentId] = useState<string | null>(null);
  const [taskSettings, setTaskSettings] = useState<EnterpriseTaskSettings | null>(null);
  const [settingsModalOpened, setSettingsModalOpened] = useState(false);
  const [settingsDraft, setSettingsDraft] = useState<EnterpriseTaskSettings | null>(null);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [sortBy, setSortBy] = useState<TaskSortField>('created_at');
  const [taskComments, setTaskComments] = useState<TaskComment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentSaving, setCommentSaving] = useState(false);

  const [opportunitiesOptions, setOpportunitiesOptions] = useState<Opportunity[]>([]);
  const [clientsOptions, setClientsOptions] = useState<Options[]>([]);
  const [responsibleOptions, setResponsibleOptions] = useState<Options[]>([]);
  const [contactsOptions, setContactsOptions] = useState<Contacts[]>([]);

  // Estados para seleção dos campos Oportunidade e Clientes
  const [selectedOpportunity, setSelectedOpportunity] = useState<string>('');
  const [selectedClient, setSelectedClient] = useState<string>('');
  const [selectedContact, setSelectedContact] = useState<string>('');

  // Estado para toggle de visualização: 'table' ou 'kanban'
  const [viewMode, setViewMode] = useState<'table' | 'kanban'>('kanban');

  // Estado para drag and drop na visão Kanban
  const [activeTask, setActiveTask] = useState<Task | null>(null);

  const taskTypeOptions = useMemo(
    () =>
      Object.entries(TASK_TYPE_I18N_KEY).map(([value, key]) => ({
        value,
        label: t(key),
      })),
    [t, i18n.language],
  );

  const stateOptions = useMemo(
    () =>
      (taskSettings?.stages ?? []).map((stage) => ({
        value: stage.id,
        label: stage.name,
      })),
    [taskSettings],
  );

  const sortOptions = useMemo(
    () =>
      (['created_at', 'deadline', 'priority', 'points'] as TaskSortField[]).map((value) => ({
        value,
        label: t(`tasks.sort.${value}`),
      })),
    [t, i18n.language],
  );

  const mentionUsers = useMemo<MentionUser[]>(
    () => responsibleOptions.map((option) => ({ id: option.value, name: option.label })),
    [responsibleOptions],
  );

  const localization = useMemo(() => buildMrtLocalization(t), [t, i18n.language]);

  const { accessToken, actualEnterprise, user } = useAuth();
  const { openPreview, previewModal } = useAttachmentPreview(accessToken);
  const sprintAutoSelectedForEnterprise = useRef<string | null>(null);

  const { atualizar } = useNotifications();
  // Funções para buscar as oportunidades e clientes (dados mockados)
  const getOpportunities = () => {
    fetch(`${Environment.VITE_API_URL}/api/v1/valid_opportunities/${actualEnterprise?.id}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${accessToken}`,
      },
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => {
        setOpportunitiesOptions(data.map((opportunity: any) => ({ value: opportunity.id, label: opportunity.name, client_ids: opportunity.client_ids ?? [] })));
      })
      .catch((error) => console.error(error));
  }

  const getClients = () => {
    fetch(`${Environment.VITE_API_URL}/api/v1/clients_options/${actualEnterprise?.id}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${accessToken}`,
      },
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => {
        const formattedData = data.map((client: any) => { return { value: client.id, label: client.legal_name } });
        setClientsOptions(formattedData.sort((a: any, b: any) => a.label.localeCompare(b.label)));
      })
      .catch((error) => console.error(error));
  }

  const getResponsibles = () => {
    fetch(`${Environment.VITE_API_URL}/api/v1/enterprise_users/${actualEnterprise?.id}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${accessToken}`,
      },
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => {
        console.log(data);
        const formattedData = data.map((opportunity: any) => { return { value: opportunity.id, label: opportunity.name } });
        setResponsibleOptions(formattedData.sort((a: any, b: any) => a.label.localeCompare(b.label)));
      })
      .catch((error) => console.error(error));
  }

  const getContacts = () => {
    fetch(`${Environment.VITE_API_URL}/api/v1/all_contacts/${actualEnterprise?.id}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${accessToken}`,
      },
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => {
        // console.log(data);
        const formattedContacts = data.map((contact: any) => (
          {
            value: contact.id,
            label: contact.first_name + ' ' + contact.last_name,
            clients: contact.clients.map((client: any) => client.client_id)
          }));
        setContactsOptions(formattedContacts.sort((a: any, b: any) => a.label.localeCompare(b.label)));
      })
      .catch((error) => console.error(error));
  }

  const filterContacts = () => {
    if (selectedClient === '') {
      return contactsOptions;
    } else {
      const options = contactsOptions.filter((contact: Contacts) => contact.clients.includes(selectedClient));
      return options
    }
  }

  const formatTaskFromApi = (task: any, index = 0): Task => ({
    id: index + 1,
    uuid: task.id,
    name: task.name,
    responsible_id: task.user_id || '',
    responsible_name: task.user_name || '',
    responsible_image: task.user_image || '',
    created_by_id: task.created_by_id,
    created_by_name: task.created_by_name || '',
    created_by_image: task.created_by_image || '',
    created_at: task.created_at,
    contact_id: task.contact_id || '',
    linked: task.linked || '',
    taskType: task.type,
    dueDate: task.date ? new Date(task.date).toISOString() : '',
    description: task.description,
    state: task.state,
    stage_id: task.stage_id || '',
    stage_name: task.stage_name || task.state,
    stage_is_done: Boolean(task.stage_is_done),
    priority: Number.parseInt(String(task.priority), 10) || 0,
    sprint_id: task.sprint_id || null,
    story_points: task.story_points ?? null,
    estimated_minutes: task.estimated_minutes ?? null,
    comment_search_text: task.comment_search_text || '',
    invoice_id: task.invoice_id || null,
    files: (task.files || []).map((file: any) => ({
      id: file.id,
      file_url: file.file_url,
      file_name: file.file_name,
      size: file.size || 0,
      file: file.file_url,
      upload_date: file.upload_date,
    })),
    deletedAt: task.deleted_at ?? null,
  });

  const getTaskSettings = async () => {
    if (!actualEnterprise?.id || !accessToken) {
      return;
    }
    try {
      const response = await fetch(
        `${Environment.VITE_API_URL}/api/v1/tasks/settings/${actualEnterprise.id}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          credentials: 'include',
        },
      );
      if (!response.ok) {
        console.error('Failed to load task settings:', response.status);
        return;
      }
      const data = await response.json();
      setTaskSettings(data);
      setSortBy(data.default_sort || 'created_at');
    } catch (error) {
      console.error(error);
    }
  };

  const getTasks = async (sortOverride?: TaskSortField) => {
    if (!actualEnterprise?.id || !accessToken) {
      return;
    }
    const activeSort = sortOverride || sortBy;
    try {
      const response = await fetch(
        `${Environment.VITE_API_URL}/api/v1/tasks/enterprise/${actualEnterprise.id}?sort_by=${activeSort}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          credentials: 'include',
        },
      );
      if (!response.ok) {
        const detail = await response.text();
        console.error('Failed to load tasks:', response.status, detail);
        return;
      }
      const data = await response.json();
      if (!Array.isArray(data)) {
        console.error('Unexpected tasks response:', data);
        return;
      }
      setTasks(data.map((task: any, index: number) => formatTaskFromApi(task, index)));
    } catch (error) {
      console.error(error);
    }
  };

  const getSprints = async () => {
    if (!actualEnterprise?.id || !accessToken) {
      return;
    }
    setSprintsLoaded(false);
    try {
      const response = await fetch(
        `${Environment.VITE_API_URL}/api/v1/sprints/enterprise/${actualEnterprise.id}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          credentials: 'include',
        },
      );
      if (!response.ok) {
        const detail = await response.text();
        console.error('Failed to load sprints:', response.status, detail);
        return;
      }
      const data = await response.json();
      if (Array.isArray(data)) {
        setSprints(data);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setSprintsLoaded(true);
    }
  };

  const getSprintMetrics = async (sprintId: string) => {
    if (!accessToken) {
      return;
    }
    try {
      const response = await fetch(
        `${Environment.VITE_API_URL}/api/v1/sprints/${sprintId}/metrics`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          credentials: 'include',
        },
      );
      if (!response.ok) {
        setSprintMetrics(null);
        return;
      }
      setSprintMetrics(await response.json());
    } catch (error) {
      console.error(error);
      setSprintMetrics(null);
    }
  };

  const assignTasksToSprint = async (taskUuids: string[], targetSprintId: string | null) => {
    if (!accessToken || taskUuids.length === 0) {
      return;
    }
    const response = await fetch(`${Environment.VITE_API_URL}/api/v1/tasks/bulk_sprint`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'include',
      body: JSON.stringify({ task_ids: taskUuids, sprint_id: targetSprintId }),
    });
    if (!response.ok) {
      alert(t('tasks.errors.bulkAssignFailed'));
      return;
    }
    await getTasks();
    if (sprintView !== 'backlog') {
      await getSprintMetrics(sprintView);
    }
  };

  const createSprint = async () => {
    if (!actualEnterprise?.id || !newSprintForm.name || !newSprintForm.start_date || !newSprintForm.end_date) {
      return;
    }
    setSavingSprint(true);
    try {
      const response = await fetch(`${Environment.VITE_API_URL}/api/v1/sprints`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        credentials: 'include',
        body: JSON.stringify({
          name: newSprintForm.name,
          enterprise_id: actualEnterprise.id,
          start_date: newSprintForm.start_date,
          end_date: newSprintForm.end_date,
          status: newSprintForm.status,
          goal: newSprintForm.goal || null,
        }),
      });
      if (!response.ok) {
        alert(t('tasks.errors.sprintCreateFailed'));
        return;
      }
      const created = await response.json();
      setSprintModalOpened(false);
      setNewSprintForm({
        name: '',
        goal: '',
        start_date: '',
        end_date: '',
        status: 'planned',
      });
      await getSprints();
      setSprintView(created.id);
    } catch (error) {
      console.error(error);
      alert(t('tasks.errors.sprintCreateFailed'));
    } finally {
      setSavingSprint(false);
    }
  };

  const closeCurrentSprint = async () => {
    if (sprintView === 'backlog') {
      return;
    }
    if (!window.confirm(t('tasks.sprints.closeConfirm'))) {
      return;
    }
    try {
      const response = await fetch(
        `${Environment.VITE_API_URL}/api/v1/sprints/${sprintView}/close?carry_over=true`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          credentials: 'include',
        },
      );
      if (!response.ok) {
        alert(t('tasks.errors.sprintCloseFailed'));
        return;
      }
      await getSprints();
      await getTasks();
      setSprintView('backlog');
      setSprintMetrics(null);
    } catch (error) {
      console.error(error);
      alert(t('tasks.errors.sprintCloseFailed'));
    }
  };

  const map_linked = async (linked: string) => {
    if (opportunitiesOptions.some((opt) => opt.label === linked)) {
      await setSelectedOpportunity(opportunitiesOptions.find((opt) => opt.label === linked)?.value || '');
      await setSelectedClient('');
    } else if (clientsOptions.some((opt) => opt.label === linked)) {
      await setSelectedClient(clientsOptions.find((opt) => opt.label === linked)?.value || '');
      await setSelectedOpportunity('');
    } else {
      await setSelectedOpportunity('');
      await setSelectedClient('');
    }
  }


  const loadTaskComments = async (taskId: string) => {
    if (!accessToken) return;
    setCommentsLoading(true);
    try {
      const response = await fetch(
        `${Environment.VITE_API_URL}/api/v1/tasks/${taskId}/comments`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          credentials: 'include',
        },
      );
      if (response.ok) {
        const data = await response.json();
        setTaskComments(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setCommentsLoading(false);
    }
  };

  const submitComment = async () => {
    if (!editingTask || !newComment.trim() || !accessToken) return;
    setCommentSaving(true);
    try {
      const response = await fetch(
        `${Environment.VITE_API_URL}/api/v1/tasks/${editingTask.uuid}/comments`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          credentials: 'include',
          body: JSON.stringify({ body: newComment.trim() }),
        },
      );
      if (response.ok) {
        const saved = await response.json();
        setNewComment('');
        await loadTaskComments(editingTask.uuid);
        setTasks((prev) =>
          prev.map((task) =>
            task.uuid === editingTask.uuid
              ? {
                  ...task,
                  comment_search_text: [task.comment_search_text, saved.body]
                    .filter(Boolean)
                    .join(' '),
                }
              : task,
          ),
        );
        await atualizar();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setCommentSaving(false);
    }
  };

  const deleteComment = async (commentId: string) => {
    if (!editingTask || !accessToken) return;
    if (!window.confirm(t('tasks.comments.deleteConfirm'))) return;
    try {
      const response = await fetch(
        `${Environment.VITE_API_URL}/api/v1/tasks/comments/${commentId}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          credentials: 'include',
        },
      );
      if (response.ok) {
        await loadTaskComments(editingTask.uuid);
        await atualizar();
      }
    } catch (error) {
      console.error(error);
    }
  };

  const canDeleteComment = (comment: TaskComment) =>
    Boolean(user && (comment.user_id === user.id || user.role === 'admin'));

  const openSettingsModal = () => {
    if (taskSettings) {
      setSettingsDraft(JSON.parse(JSON.stringify(taskSettings)));
    }
    setSettingsModalOpened(true);
  };

  const saveTaskSettings = async () => {
    if (!actualEnterprise?.id || !settingsDraft || !accessToken) return;
    setSettingsSaving(true);
    try {
      const response = await fetch(
        `${Environment.VITE_API_URL}/api/v1/tasks/settings/${actualEnterprise.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          credentials: 'include',
          body: JSON.stringify({
            default_sort: settingsDraft.default_sort,
            stages: settingsDraft.stages.map((stage, index) => ({
              id: stage.id || undefined,
              name: stage.name,
              position: index,
              is_done: stage.is_done,
            })),
          }),
        },
      );
      if (response.ok) {
        const data = await response.json();
        setTaskSettings(data);
        setSortBy(data.default_sort || 'created_at');
        setSettingsModalOpened(false);
        await getTasks(data.default_sort);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setSettingsSaving(false);
    }
  };

  const openModal = (task?: Task) => {
    if (task) {
      seteditingTask(task);
      const selectedStage = taskSettings?.stages.find((stage) => stage.id === task.stage_id);
      settaskForm({
        name: task.name,
        responsible_id: task.responsible_id,
        responsible_name: task.responsible_name,
        responsible_image: task.responsible_image,
        created_by_id: task.created_by_id,
        created_by_name: task.created_by_name,
        created_by_image: task.created_by_image,
        contact_id: task.contact_id,
        linked: task.linked,
        taskType: task.taskType,
        dueDate: task.dueDate,
        description: task.description,
        state: task.state,
        stage_id: task.stage_id || selectedStage?.id || '',
        priority: task.priority,
        sprint_id: task.sprint_id,
        story_points: task.story_points,
        estimated_minutes: task.estimated_minutes,
        files: task.files,
      });
      void loadTaskComments(task.uuid);
      map_linked(task.linked);
    } else {
      seteditingTask(null);
      setTaskComments([]);
      const firstStage = taskSettings?.stages[0];
      settaskForm({
        ...initialtaskForm,
        taskType: 'tarefa',
        sprint_id: sprintView !== 'backlog' ? sprintView : null,
        stage_id: firstStage?.id || '',
        state: firstStage?.name || 'To do',
      });
      setSelectedOpportunity('');
      setSelectedClient('');
      setSelectedContact('');
    }
    setModalOpened(true);
    setAutoImportFile(null);
    setImportErrors([]);
    setImportNotification(null);
    setErrorMessage('');
    setAttachmentErrors([]);
    setNewComment('');
  };

  const isImageFile = (fileName: string) =>
    /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(fileName);

  const getTaskAttachmentSource = (attachment: TaskAttachment): AttachmentFileSource | null => {
    const filePath =
      attachment.file_url || (typeof attachment.file === 'string' ? attachment.file : undefined);
    const localFile = attachment.file instanceof File ? attachment.file : undefined;
    return attachmentSourceFromFields(attachment.file_name, filePath, localFile);
  };

  const downloadTaskAttachment = async (attachment: TaskAttachment) => {
    const source = getTaskAttachmentSource(attachment);
    if (!source) return;
    setDownloadingAttachmentId(attachment.id);
    try {
      await downloadAttachment(source, accessToken);
    } catch (error) {
      console.error(error);
      alert(t('tasks.errors.openAttachmentFailed'));
    } finally {
      setDownloadingAttachmentId(null);
    }
  };

  const sendTaskFiles = async (files: TaskAttachment[]): Promise<TaskAttachment[]> => {
    const uploaded: TaskAttachment[] = [];
    for (const attachment of files) {
      if (attachment.file instanceof File) {
        const formData = new FormData();
        formData.append('file', attachment.file);
        const endpoint = isImageFile(attachment.file_name)
          ? '/api/v1/upload-image'
          : '/api/v1/upload-file';
        const response = await fetch(`${Environment.VITE_API_URL}${endpoint}`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${accessToken}` },
          credentials: 'include',
          body: formData,
        });
        if (!response.ok) {
          throw new Error(`Failed to upload ${attachment.file_name}`);
        }
        const data = await response.json();
        const fileUrl = data.image_url || data.file_url;
        uploaded.push({
          id: attachment.id,
          file_name: attachment.file_name,
          size: attachment.size,
          file_url: fileUrl,
          file: fileUrl,
        });
      } else {
        uploaded.push({
          id: attachment.id,
          file_name: attachment.file_name,
          size: attachment.size,
          file_url: attachment.file_url || (typeof attachment.file === 'string' ? attachment.file : ''),
          file: attachment.file,
        });
      }
    }
    return uploaded;
  };

  const buildTaskPayload = (task: taskForm, uploadedFiles: TaskAttachment[]) => {
    let opportunity_id: string | null = null;
    let client_id: string | null = null;
    let contact_id: string | null = null;

    if (opportunitiesOptions.some((opt) => opt.label === task.linked || opt.value === task.linked)) {
      opportunity_id =
        opportunitiesOptions.find((opt) => opt.label === task.linked || opt.value === task.linked)?.value || null;
    } else if (clientsOptions.some((opt) => opt.label === task.linked || opt.value === task.linked)) {
      client_id =
        clientsOptions.find((opt) => opt.label === task.linked || opt.value === task.linked)?.value || null;
    }

    if (task.contact_id) {
      contact_id = task.contact_id;
    }

    const selectedStage = taskSettings?.stages.find((stage) => stage.id === task.stage_id);
    return {
      enterprise_id: actualEnterprise!.id,
      user_id: task.responsible_id || null,
      opportunity_id,
      client_id,
      contact_id,
      name: task.name,
      type: task.taskType || 'tarefa',
      description: task.description,
      date: task.dueDate || null,
      state: selectedStage?.is_done ? 'Done' : selectedStage?.position === 0 ? 'To do' : 'Doing',
      stage_id: task.stage_id || null,
      priority: task.priority || 0,
      sprint_id: task.sprint_id,
      story_points: task.story_points,
      estimated_minutes: task.estimated_minutes,
      files: uploadedFiles.map((file) => ({
        file_url: file.file_url || (typeof file.file === 'string' ? file.file : ''),
        file_name: file.file_name,
        size: file.size,
      })),
    };
  };

  const {
    getRootProps: getAttachmentRootProps,
    getInputProps: getAttachmentInputProps,
    isDragActive: isAttachmentDragActive,
  } = useDropzone({
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'],
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/plain': ['.txt'],
    },
    multiple: true,
    maxSize: 50 * 1024 * 1024,
    onDrop: (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      const newErrors: string[] = [];
      const newAttachments = [...taskForm.files];
      const availableSlots = 10 - taskForm.files.length;

      if (acceptedFiles.length > availableSlots) {
        newErrors.push(
          t('tasks.errors.maxAttachments', { count: acceptedFiles.length - availableSlots }),
        );
      }

      acceptedFiles.slice(0, availableSlots).forEach((file) => {
        newAttachments.push({
          id: `${Date.now()}-${Math.random()}`,
          file_name: file.name,
          file,
          size: file.size,
        });
      });

      fileRejections.forEach(({ file, errors }) => {
        errors.forEach((error) => {
          if (error.code === 'file-too-large') {
            newErrors.push(t('tasks.errors.fileTooLarge', { name: file.name }));
          } else {
            newErrors.push(`${file.name}: ${error.message}`);
          }
        });
      });

      setAttachmentErrors(newErrors);
      settaskForm({ ...taskForm, files: newAttachments });
    },
  });

  const removeAttachment = (id: string) => {
    settaskForm((prev) => ({
      ...prev,
      files: prev.files.filter((file) => file.id !== id),
    }));
  };

  const isFormValid = () => {
    return (
      taskForm.name.trim() !== '' &&
      taskForm.description.trim() !== ''
    );
  };

  const formatExcelTask = (task: taskForm) => {
    return buildTaskPayload(task, task.files);
  };

  const taskImportHeaders = useMemo(
    () => ({
      name: t('tasks.columns.title'),
      responsible_name: t('tasks.columns.assignedTo'),
      linked: t('tasks.columns.linkedTo'),
      taskType: t('tasks.columns.type'),
      dueDate: t('tasks.columns.deadline'),
      description: t('tasks.columns.description'),
      priority: t('tasks.columns.priority'),
      story_points: t('tasks.sprints.form.storyPoints'),
      estimated_minutes: t('tasks.sprints.form.estimatedTime'),
      stage_name: t('tasks.columns.status'),
    }),
    [t, i18n.language],
  );

  const resolveTaskType = (raw: unknown): string => {
    const text = String(raw ?? '').trim();
    if (!text) return 'tarefa';
    const byValue = taskTypeOptions.find((option) => option.value === text);
    if (byValue) return byValue.value;
    const byLabel = taskTypeOptions.find((option) => option.label.toLowerCase() === text.toLowerCase());
    return byLabel?.value ?? text;
  };

  const resolveStageId = (raw: unknown): string => {
    const text = String(raw ?? '').trim();
    if (!text) return taskSettings?.stages[0]?.id ?? '';
    const byId = taskSettings?.stages.find((stage) => stage.id === text);
    if (byId) return byId.id;
    const byName = taskSettings?.stages.find((stage) => stage.name.toLowerCase() === text.toLowerCase());
    return byName?.id ?? taskSettings?.stages[0]?.id ?? '';
  };

  const rowToTaskForm = (row: Record<string, unknown>): taskForm => {
    const responsibleLabel = String(readRowCell(row, taskImportHeaders.responsible_name, 'Assigned To') ?? '');
    const responsible = responsibleOptions.find((option) => option.label === responsibleLabel);
    const stageId = resolveStageId(readRowCell(row, taskImportHeaders.stage_name, 'Status', 'stage_id'));
    const selectedStage = taskSettings?.stages.find((stage) => stage.id === stageId);
    const dueRaw = readRowCell(row, taskImportHeaders.dueDate, 'Deadline');
    return {
      name: String(readRowCell(row, taskImportHeaders.name, 'Title') ?? ''),
      responsible_id: responsible?.value ?? '',
      responsible_name: responsible?.label ?? responsibleLabel,
      responsible_image: 'https://cdn-icons-png.flaticon.com/512/219/219990.png',
      created_by_id: user?.id ?? '',
      created_by_name: user?.name ?? '',
      created_by_image: user?.photo ?? '',
      contact_id: '',
      linked: String(readRowCell(row, taskImportHeaders.linked, 'Linked To') ?? ''),
      taskType: resolveTaskType(readRowCell(row, taskImportHeaders.taskType, 'Type')),
      dueDate: dueRaw ? new Date(String(dueRaw)).toISOString() : '',
      description: String(readRowCell(row, taskImportHeaders.description, 'Description') ?? ''),
      state: selectedStage?.is_done ? 'Done' : selectedStage?.position === 0 ? 'To do' : 'Doing',
      stage_id: stageId,
      priority: Number(readRowCell(row, taskImportHeaders.priority, 'Priority') ?? 0) || 0,
      sprint_id: sprintView !== 'backlog' ? sprintView : null,
      story_points: readRowCell(row, taskImportHeaders.story_points, 'Story Points') != null
        ? Number(readRowCell(row, taskImportHeaders.story_points, 'Story Points'))
        : null,
      estimated_minutes: readRowCell(row, taskImportHeaders.estimated_minutes, 'Estimated Time') != null
        ? Number(readRowCell(row, taskImportHeaders.estimated_minutes, 'Estimated Time'))
        : null,
      files: [],
    };
  };

  const validateTaskImportForm = (form: taskForm, line: number): string[] => {
    const rowErrors: string[] = [];
    if (!form.name.trim()) rowErrors.push(t('tasks.form.title'));
    if (!form.description.trim()) rowErrors.push(t('tasks.form.description'));
    if (rowErrors.length > 0) {
      return [`Line ${line}: ${rowErrors.join(' ')}`];
    }
    return [];
  };

  const executeTaskImport = async (
    createForms: taskForm[],
    updateEntries: Array<{ id: string; form: taskForm }>,
    deleteIds: string[],
  ) => {
    const ok = await executeImportUpsert({
      createPayloads: createForms.map((form) => buildTaskPayload(form, [])),
      updateEntries: updateEntries.map((entry) => ({
        id: entry.id,
        payload: buildTaskPayload(entry.form, []),
      })),
      deleteIds,
      bulkCreateUrl: `${Environment.VITE_API_URL}/api/v1/tasks_bulk`,
      updateUrl: (id) => `${Environment.VITE_API_URL}/api/v1/tasks/${id}`,
      deleteUrl: (id) => `${Environment.VITE_API_URL}/api/v1/tasks/${id}`,
      buildRequest: ({ method, body }) => ({
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        credentials: 'include',
        body: body !== undefined ? JSON.stringify(body) : undefined,
      }),
    });

    if (!ok) {
      setImportNotification({ message: t('common.import.failed'), color: 'red' });
      return;
    }

    setImportNotification({ message: t('common.import.success'), color: 'green' });
    setModalOpened(false);
    setImportModalOpened(false);
    await getTasks();
    atualizar();
  };

  const handleAutoImport = async () => {
    if (!autoImportFile) return;
    try {
      const rawData = await readExcelFile(autoImportFile);
      const existingIds = new Set(tasks.map((task) => task.uuid));
      const classified = classifyImportRows(rawData, existingIds, t);
      const split = splitImportRowsById(classified.rowsForUpsert, existingIds);
      const errors: string[] = [...classified.errors];

      const headerToFormKey = Object.fromEntries(
        Object.entries(taskImportHeaders).map(([formKey, header]) => [header, formKey]),
      ) as Record<string, keyof taskForm & string>;

      const createForms: taskForm[] = [];
      for (const row of split.creates) {
        const line = rawData.indexOf(row) + 2;
        const form = rowToTaskForm(row);
        errors.push(...validateTaskImportForm(form, line));
        createForms.push(form);
      }

      const updateEntries: Array<{ id: string; form: taskForm }> = [];
      for (const entry of split.updates) {
        const line = rawData.indexOf(entry.row) + 2;
        const existing = tasks.find((task) => task.uuid === entry.id);
        if (!existing) continue;
        const patch = rowToTaskForm(entry.row);
        const merged = mergeDefinedFields(
          {
            name: existing.name,
            responsible_id: existing.responsible_id,
            responsible_name: existing.responsible_name,
            responsible_image: existing.responsible_image,
            created_by_id: existing.created_by_id,
            created_by_name: existing.created_by_name,
            created_by_image: existing.created_by_image,
            contact_id: existing.contact_id,
            linked: existing.linked,
            taskType: existing.taskType,
            dueDate: existing.dueDate,
            description: existing.description,
            state: existing.state,
            stage_id: existing.stage_id,
            priority: existing.priority,
            sprint_id: existing.sprint_id,
            story_points: existing.story_points,
            estimated_minutes: existing.estimated_minutes,
            files: existing.files,
          },
          patch,
          formKeysPresentInRow(entry.row, headerToFormKey),
        );
        errors.push(...validateTaskImportForm(merged, line));
        updateEntries.push({ id: entry.id, form: merged });
      }

      if (errors.length > 0) {
        setImportNotification({ message: t('common.import.someFailed'), color: 'red' });
        setImportErrors(errors);
        return;
      }

      setImportConfirm({
        newCount: createForms.length,
        updateCount: updateEntries.length,
        deleteCount: classified.deleteIds.length,
        run: () => executeTaskImport(createForms, updateEntries, classified.deleteIds),
      });
    } catch (error) {
      console.error(error);
      setImportNotification({ message: t('common.import.processFailed'), color: 'red' });
    }
  };

  const saveTask = async () => {
    if (!isFormValid()) {
      alert(t('tasks.errors.fillRequired'));
      return;
    }
    if (!actualEnterprise?.id) {
      setErrorMessage(t('tasks.errors.noEnterprise'));
      return;
    }
    setSaving(true);
    setErrorMessage('');
    try {
      const uploadedFiles = await sendTaskFiles(taskForm.files);
      const payload = buildTaskPayload(taskForm, uploadedFiles);
      const response = await fetch(`${Environment.VITE_API_URL}/api/v1/tasks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        let errorData: { detail?: unknown } = {};
        try {
          errorData = await response.json();
        } catch {
          // Non-JSON error body
        }
        setErrorMessage(
          parseApiErrorDetail(errorData.detail, t('tasks.errors.createFailed', { status: response.status })),
        );
        return;
      }
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        setErrorMessage(t('tasks.errors.unexpectedResponse'));
        return;
      }
      const created = await response.json();
      if (!created?.id) {
        setErrorMessage(t('tasks.errors.noTaskId'));
        return;
      }
      setModalOpened(false);
      await getTasks();
      atualizar();
    } catch (error) {
      console.error(error);
      setErrorMessage(t('tasks.errors.uploadFailed'));
    } finally {
      setSaving(false);
    }
  };

  const updateTask = async () => {
    if (!isFormValid() || !editingTask) {
      alert(t('tasks.errors.fillRequired'));
      return;
    }
    if (!actualEnterprise?.id) {
      setErrorMessage('No enterprise selected.');
      return;
    }
    setSaving(true);
    setErrorMessage('');
    try {
      const uploadedFiles = await sendTaskFiles(taskForm.files);
      const payload = buildTaskPayload(taskForm, uploadedFiles);
      const response = await fetch(`${Environment.VITE_API_URL}/api/v1/tasks/${editingTask.uuid}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        let errorData: { detail?: unknown } = {};
        try {
          errorData = await response.json();
        } catch {
          // Non-JSON error body
        }
        setErrorMessage(
          parseApiErrorDetail(errorData.detail, t('tasks.errors.updateFailed', { status: response.status })),
        );
        return;
      }
      setModalOpened(false);
      getTasks();
      await atualizar();
    } catch (error) {
      console.error(error);
      setErrorMessage(t('tasks.errors.updateUploadFailed'));
    } finally {
      setSaving(false);
    }
  };

  // Exclui task
  const deleteTask = () => {
    if (!editingTask) return;
    fetch(`${Environment.VITE_API_URL}/api/v1/tasks/${editingTask?.uuid}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((data) => {
        console.log(data);
        setModalOpened(false);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        getTasks();
      });
  };

  const selectedSprint = useMemo(
    () => (sprintView === 'backlog' ? null : sprints.find((sprint) => sprint.id === sprintView) || null),
    [sprintView, sprints],
  );

  const sprintOptions = useMemo(
    () => [
      { value: '', label: t('tasks.sprints.form.noSprint') },
      ...sprints
        .filter((sprint) => sprint.status !== 'completed')
        .map((sprint) => ({
          value: sprint.id,
          label: `${sprint.name} (${t(SPRINT_STATUS_I18N_KEY[sprint.status])})`,
        })),
    ],
    [sprints, t, i18n.language],
  );

  const sprintViewOptions = useMemo(
    () => [
      { value: 'backlog', label: t('tasks.sprints.backlog') },
      ...sprints.map((sprint) => ({
        value: sprint.id,
        label: `${sprint.name}${sprint.status === 'active' ? ' •' : ''}`,
      })),
    ],
    [sprints, t, i18n.language],
  );

  const visibleTasks = useMemo(() => {
    let filtered = tasks;
    if (sprintView === 'backlog') {
      filtered = tasks.filter((task) => !task.sprint_id);
    } else {
      filtered = tasks.filter((task) => task.sprint_id === sprintView);
    }
    filtered = filtered.filter((task) => taskMatchesSearch(task, taskSearchQuery));
    return sortTasks(filtered.filter(task => matchesPageMonth(task.dueDate, selectedMonth)), sortBy);
  }, [tasks, sprintView, sortBy, taskSearchQuery, selectedMonth]);

  const visibleSprintMetrics = useMemo(() => {
    if (!sprintMetrics) return null;
    const done = visibleTasks.filter(task => task.stage_is_done);
    return { ...sprintMetrics,
      committed_points: visibleTasks.reduce((sum,task)=>sum+(task.story_points||0),0),
      completed_points: done.reduce((sum,task)=>sum+(task.story_points||0),0),
      estimated_minutes: visibleTasks.reduce((sum,task)=>sum+(task.estimated_minutes||0),0),
      completed_minutes: done.reduce((sum,task)=>sum+(task.estimated_minutes||0),0),
      total_tasks: visibleTasks.length, done_tasks: done.length,
      completion_rate: visibleTasks.length ? Math.round(done.length/visibleTasks.length*100) : 0,
    };
  }, [sprintMetrics, visibleTasks]);

  const droppableSprints = useMemo(
    () => sprints.filter((sprint) => sprint.status !== 'completed'),
    [sprints],
  );

  const isReadOnlySprint = selectedSprint?.status === 'completed';

  const columns = useMemo<MRT_ColumnDef<Task>[]>(() => enableGlobalSearchOnColumns([
    {
      accessorKey: 'name',
      header: t('tasks.columns.title'),
      mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
      mantineTableBodyCellProps: { sx: { textAlign: 'left' } },
    },
    {
      accessorKey: 'responsible_name',
      header: t('tasks.columns.assignedTo'),
      mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
      mantineTableBodyCellProps: { sx: { textAlign: 'left' } },
    },
    {
      accessorKey: 'created_by_name',
      header: t('tasks.columns.createdBy'),
      mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
      mantineTableBodyCellProps: { sx: { textAlign: 'left' } },
    },
    {
      accessorKey: 'created_at',
      header: t('tasks.columns.createdAt'),
      mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
      mantineTableBodyCellProps: { sx: { textAlign: 'center' } },
      Cell: ({ cell }) => formatAppDate(cell.getValue<string>()),
    },
    {
      accessorKey: 'linked',
      header: t('tasks.columns.linkedTo'),
      mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
      mantineTableBodyCellProps: { sx: { textAlign: 'left' } },
    },
    {
      accessorKey: 'taskType',
      header: t('tasks.columns.type'),
      mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
      mantineTableBodyCellProps: { sx: { textAlign: 'center' } },
      Cell: ({ cell }) => {
        const taskType = cell.getValue<string>();
        return taskTypeOptions.find((type) => type.value === taskType)?.label || taskType;
      },
    },
    {
      accessorKey: 'dueDate',
      header: t('tasks.columns.deadline'),
      mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
      mantineTableBodyCellProps: { sx: { textAlign: 'center' } },
      Cell: ({ cell }) => formatAppDate(cell.getValue<string>()),
    },
    {
      accessorKey: 'priority',
      header: t('tasks.columns.priority'),
      mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
      mantineTableBodyCellProps: { sx: { textAlign: 'center' } },
    },
    {
      accessorKey: 'story_points',
      header: t('tasks.sprints.form.storyPoints'),
      mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
      mantineTableBodyCellProps: { sx: { textAlign: 'center' } },
      Cell: ({ cell }) => cell.getValue<number | null>() ?? '—',
    },
    {
      accessorKey: 'estimated_minutes',
      header: t('tasks.sprints.form.estimatedTime'),
      mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
      mantineTableBodyCellProps: { sx: { textAlign: 'center' } },
      Cell: ({ cell }) => cell.getValue<number | null>() ?? '—',
    },
    {
      accessorKey: 'description',
      header: t('tasks.columns.description'),
      mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
      mantineTableBodyCellProps: { sx: { textAlign: 'left' } },
    },
    {
      accessorKey: 'stage_name',
      header: t('tasks.columns.status'),
      mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
      mantineTableBodyCellProps: { sx: { textAlign: 'center' } },
      Cell: ({ cell }) => cell.getValue<string>() || '—',
    },
    {
      accessorFn: (row) => row.files.length,
      id: 'attachments',
      header: t('tasks.columns.files'),
      mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
      mantineTableBodyCellProps: { sx: { textAlign: 'center' } },
    },
  ], ['name', 'description']), [t, i18n.language, taskTypeOptions]);

  const openImportModal = () => {
    setImportModalOpened(true);
    setAutoImportFile(null);
    setImportErrors([]);
    setImportNotification(null);
  };

  const { table, exportModal } = useExportableTable(
    {
      columns,
      data: visibleTasks,
      getRowId: (row) => row.uuid,
      localization,
      defaultColumn: { size: 40, minSize: 30, maxSize: 600 },
      enableColumnFilters: true,
      enableSorting: true,
      enableFullScreenToggle: true,
      enableGrouping: false,
      enableColumnActions: false,
      enablePagination: true,
      enableBottomToolbar: true,
      enableRowVirtualization: false,
      enableRowSelection: !isReadOnlySprint,
      onRowSelectionChange: setRowSelection,
      state: { rowSelection },
      initialState: {
        showGlobalFilter: false,
        pagination: {
          pageSize: 20,
          pageIndex: 0,
        },
      },
      mantineTableContainerProps: mrtTableContainerProps,
      mantineTableProps: {
        sx: {
          display: 'table',
          tableLayout: 'auto',
          borderCollapse: 'separate',
          '& .mantine-TableHeadCell-Content': {
            justifyContent: 'center',
            alignItems: 'center',
            padding: '1px 1px',
          },
          '& .mantine-TextInput-input': {
            backgroundColor: 'rgba(255, 255, 255, 0.6)',
            borderRadius: '4px',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            color: '#000',
          },
          '& th, & td': {
            borderRadius: '4px',
          },
          '& thead': {
            position: 'sticky',
            top: 0,
            zIndex: 100,
            backgroundColor: 'rgba(48,113,91,0.9)',
            color: '#fff',
          },
        },
      },
      mantineTableBodyRowProps: ({ row }: { row: MRT_Row<Task> }) => ({
        onDoubleClick: () => handleRowDoubleClick(row.original),
        style: { cursor: 'pointer' },
      }),
    },
    {
      idField: 'uuid',
      filenamePrefix: 'tasks',
      sheetName: 'Tasks',
      getDeletedValue: (row) => Boolean(row.deletedAt),
      getCellValue: (row, columnId) => {
        if (columnId === 'taskType') {
          return taskTypeOptions.find((type) => type.value === row.taskType)?.label ?? row.taskType;
        }
        if (columnId === 'created_at' || columnId === 'dueDate') {
          const value = row[columnId as keyof Task];
          return typeof value === 'string' ? value.split('T')[0] : value;
        }
        if (columnId === 'attachments') return row.files.length;
        return row[columnId as keyof Task];
      },
      onOpenImport: openImportModal,
    },
  );

  const importModal = (
    <TableExcelImportModal
      opened={importModalOpened}
      onClose={() => setImportModalOpened(false)}
      onImport={handleAutoImport}
      file={autoImportFile}
      onFileChange={(file) => setAutoImportFile(file)}
      errors={importErrors}
      notification={importNotification}
    />
  );

  const handleRowDoubleClick = (rowData: Task) => {
    openModal(rowData);
  };

  // Handlers do DndContext para a visão Kanban
  const handleDragStart = (event: any) => {
    const id = event.active.id; // formato "task-<id>"
    const taskId = parseInt(id.split('-')[1], 10);
    const task = tasks.find((a) => a.id === taskId) || null;
    setActiveTask(task);
    map_linked(task?.linked || '');
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;

    if (over && active.id) {
      const taskId = parseInt(active.id.split('-')[1], 10);
      const updatedTask = tasks.find((a) => a.id === taskId);
      if (!updatedTask) {
        setActiveTask(null);
        return;
      }

      if (String(over.id).startsWith('sprint-')) {
        const targetSprintId = String(over.id).replace('sprint-', '');
        setTasks((prev) =>
          prev.map((task) =>
            task.uuid === updatedTask.uuid ? { ...task, sprint_id: targetSprintId } : task,
          ),
        );
        void assignTasksToSprint([updatedTask.uuid], targetSprintId);
        setActiveTask(null);
        return;
      }

      const newStageId = String(over.id);
      const targetStage = taskSettings?.stages.find((stage) => stage.id === newStageId);
      if (!targetStage || updatedTask.stage_id === newStageId) {
        setActiveTask(null);
        return;
      }
      setTasks((prev) =>
        prev.map((a) =>
          a.id === taskId
            ? {
                ...a,
                stage_id: newStageId,
                stage_name: targetStage.name,
                stage_is_done: targetStage.is_done,
                state: targetStage.is_done ? 'Done' : targetStage.position === 0 ? 'To do' : 'Doing',
              }
            : a,
        ),
      );
      if (updatedTask) {
        fetch(`${Environment.VITE_API_URL}/api/v1/tasks/${updatedTask.uuid}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          credentials: 'include',
          body: JSON.stringify(
            formatExcelTask({
              name: updatedTask.name,
              responsible_id: updatedTask.responsible_id,
              responsible_name: updatedTask.responsible_name,
              responsible_image: updatedTask.responsible_image,
              created_by_id: updatedTask.created_by_id,
              created_by_name: updatedTask.created_by_name,
              created_by_image: updatedTask.created_by_image,
              contact_id: updatedTask.contact_id,
              linked: updatedTask.linked,
              taskType: updatedTask.taskType,
              dueDate: updatedTask.dueDate,
              description: updatedTask.description,
              state: targetStage.is_done ? 'Done' : targetStage.position === 0 ? 'To do' : 'Doing',
              stage_id: newStageId,
              priority: updatedTask.priority,
              sprint_id: updatedTask.sprint_id,
              story_points: updatedTask.story_points,
              estimated_minutes: updatedTask.estimated_minutes,
              files: updatedTask.files,
            }),
          ),
        })
          .then((res) => res.json())
          .then((data) => {
            console.log(data);
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            getTasks();
            if (sprintView !== 'backlog') {
              getSprintMetrics(sprintView);
            }
          });
      }
    }
    setActiveTask(null);
  };

  const handleBulkAssign = async () => {
    const taskUuids = Object.keys(rowSelection).filter((key) => rowSelection[key]);
    await assignTasksToSprint(taskUuids, bulkSprintId);
    setRowSelection({});
  };

  useEffect(() => {
    sprintAutoSelectedForEnterprise.current = null;
    setSprintMetrics(null);
    setSprints([]);
    setSprintsLoaded(false);
  }, [actualEnterprise?.id]);

  useEffect(() => {
    if (!actualEnterprise?.id || !sprintsLoaded) {
      return;
    }
    if (sprintAutoSelectedForEnterprise.current === actualEnterprise.id) {
      return;
    }
    // API returns sprints ordered by start_date desc — first is the latest.
    setSprintView(sprints.length > 0 ? sprints[0].id : 'backlog');
    sprintAutoSelectedForEnterprise.current = actualEnterprise.id;
  }, [sprints, actualEnterprise?.id, sprintsLoaded]);

  useEffect(() => {
    document.title = t('tasks.documentTitle');
    const loadTaskData = async () => {
      await getTaskSettings();
      await getTasks();
    };
    void loadTaskData();
    getSprints();
    getClients();
    getOpportunities();
    getResponsibles();
    getContacts();
  }, [accessToken, actualEnterprise]);

  useEffect(() => {
    if (sprintView === 'backlog') {
      setSprintMetrics(null);
      return;
    }
    getSprintMetrics(sprintView);
  }, [sprintView, tasks, accessToken]);

  useEffect(() => {
    openedTaskFromUrl.current = false;
  }, [taskIdFromUrl]);

  useEffect(() => {
    if (!taskIdFromUrl || openedTaskFromUrl.current || tasks.length === 0) {
      return;
    }
    const matchedTask = tasks.find((task) => task.uuid === taskIdFromUrl);
    if (matchedTask) {
      openedTaskFromUrl.current = true;
      openModal(matchedTask);
    }
  }, [taskIdFromUrl, tasks]);

  const closeTaskModal = () => {
    setModalOpened(false);
    if (taskIdFromUrl) {
      navigate('/tasks', { replace: true });
    }
  };

  return (
    <>

      {/* Cabeçalho */}
      <Group position="apart" align="center" mb="md">
        <Group spacing="lg">
          <Title order={1}>{t('tasks.title')}</Title>
          <InvoiceMonthFilter value={selectedMonth} onChange={setSelectedMonth} label={t('tasks.columns.deadline')}/>
        </Group>
        <Group>
          <Select
            label={t('tasks.sort.label')}
            data={sortOptions}
            value={sortBy}
            onChange={(value) => {
              const nextSort = (value as TaskSortField) || 'created_at';
              setSortBy(nextSort);
              void getTasks(nextSort);
            }}
            style={{ minWidth: 180 }}
          />
          {user?.role === 'admin' && (
            <ActionIcon
              variant="light"
              color="green"
              size="lg"
              title={t('tasks.settings.title')}
              onClick={openSettingsModal}
              style={{ alignSelf: 'flex-end' }}
            >
              <IconSettings size={20} />
            </ActionIcon>
          )}
          <SegmentedControl
            value={viewMode}
            onChange={(value) => setViewMode(value as 'table' | 'kanban')}
            data={[
              { label: t('common.view.table'), value: 'table' },
              { label: t('common.view.kanban'), value: 'kanban' },
            ]}
          />
          <Button onClick={() => openModal()}>{t('tasks.addTask')}</Button>
        </Group>
      </Group>

      <TextInput
        label={t('tasks.search.label')}
        placeholder={t('tasks.search.placeholder')}
        icon={<IconSearch size={16} />}
        value={taskSearchQuery}
        onChange={(event) => setTaskSearchQuery(event.currentTarget.value)}
        mb="md"
      />

      <Group position="apart" align="flex-end" mb="sm">
        <Select
          label={t('tasks.sprints.selectSprint')}
          data={sprintViewOptions}
          value={sprintView}
          onChange={(value) => setSprintView((value as SprintView) || 'backlog')}
          style={{ minWidth: 260 }}
          searchable
          filter={filterSelectByLabel}
        />
        <Group>
          {selectedSprint && selectedSprint.status !== 'completed' && (
            <Button variant="outline" color="red" onClick={closeCurrentSprint}>
              {t('tasks.sprints.closeSprint')}
            </Button>
          )}
          <Button leftIcon={<IconPlus size={16} />} onClick={() => setSprintModalOpened(true)}>
            {t('tasks.sprints.newSprint')}
          </Button>
        </Group>
      </Group>

      {selectedSprint?.goal && (
        <Paper withBorder p="sm" mb="sm" radius="md" style={{ borderColor: '#30715B' }}>
          <Group spacing="xs">
            <IconTarget size={18} color="#30715B" />
            <Text size="sm" weight={600}>
              {selectedSprint.goal}
            </Text>
          </Group>
        </Paper>
      )}

      {visibleSprintMetrics && sprintView !== 'backlog' && (
        <Paper withBorder p="sm" mb="md" radius="md">
          <Group position="apart">
            <Box>
              <Text size="xs" color="dimmed">{t('tasks.sprints.metrics.points')}</Text>
              <Text weight={600}>
                {t('tasks.sprints.metrics.pointsDone', {
                  done: visibleSprintMetrics.completed_points,
                  total: visibleSprintMetrics.committed_points,
                })}
              </Text>
            </Box>
            <Box>
              <Text size="xs" color="dimmed">{t('tasks.sprints.metrics.time')}</Text>
              <Text weight={600}>
                {t('tasks.sprints.metrics.timeDone', {
                  done: visibleSprintMetrics.completed_minutes,
                  total: visibleSprintMetrics.estimated_minutes,
                })}
              </Text>
            </Box>
            <Box>
              <Text size="xs" color="dimmed">{t('tasks.sprints.metrics.tasks')}</Text>
              <Text weight={600}>
                {t('tasks.sprints.metrics.tasksDone', {
                  done: visibleSprintMetrics.done_tasks,
                  total: visibleSprintMetrics.total_tasks,
                })}
              </Text>
            </Box>
            <Box>
              <Text size="xs" color="dimmed">{t('tasks.sprints.metrics.completionLabel')}</Text>
              <Text weight={600}>
                {t('tasks.sprints.metrics.completion', { rate: visibleSprintMetrics.completion_rate })}
              </Text>
            </Box>
            {selectedSprint?.status === 'active' && (
              <Box>
                <Text size="xs" color="dimmed">{t('tasks.sprints.metrics.daysLeftLabel')}</Text>
                <Text weight={600}>
                  {t('tasks.sprints.metrics.daysLeft', { count: visibleSprintMetrics.days_remaining })}
                </Text>
              </Box>
            )}
          </Group>
        </Paper>
      )}


      {viewMode === 'table' && !isReadOnlySprint && (
        <Group mb="sm">
          <Select
            placeholder={t('tasks.sprints.assignToSprint')}
            data={sprintOptions}
            value={bulkSprintId}
            onChange={setBulkSprintId}
            style={{ minWidth: 220 }}
            clearable
          />
          <Button
            variant="light"
            disabled={!bulkSprintId || Object.keys(rowSelection).length === 0}
            onClick={handleBulkAssign}
          >
            {t('tasks.sprints.assignToSprint')}
          </Button>
          {sprintView !== 'backlog' && (
            <Button
              variant="subtle"
              disabled={Object.keys(rowSelection).length === 0}
              onClick={() => {
                const taskUuids = Object.keys(rowSelection).filter((key) => rowSelection[key]);
                assignTasksToSprint(taskUuids, null);
                setRowSelection({});
              }}
            >
              {t('tasks.sprints.removeFromSprint')}
            </Button>
          )}
        </Group>
      )}

      {/* Conteúdo Principal */}
      {viewMode === 'table' ? (
        <div style={tableShellStyle}>
          <ExportableMantineReactTable table={table} exportModal={exportModal} importModal={importModal} />
        </div>
      ) : (
        <DndContext
          collisionDetection={pointerWithin}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          {sprintView === 'backlog' && droppableSprints.length > 0 && (
            <Stack spacing="xs" mb="md">
              <Text size="sm" color="dimmed">{t('tasks.sprints.dragToSprint')}</Text>
              <Group spacing="sm" style={{ overflowX: 'auto', flexWrap: 'nowrap' }}>
                {droppableSprints.map((sprint) => (
                  <SprintDropZone key={sprint.id} sprint={sprint} />
                ))}
              </Group>
            </Stack>
          )}
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-start',
              width: 'max-content',
              minWidth: '100%',
            }}
          >
            {(taskSettings?.stages ?? []).map((stage) => (
              <KanbanColumn
                key={stage.id}
                stage={stage}
                sortBy={sortBy}
                tasks={visibleTasks.filter((act) => act.stage_id === stage.id)}
                onCardClick={(task) => openModal(task)}
              />
            ))}
          </div>
          <DragOverlay>
            {activeTask ? (
              <TaskCard
                task={activeTask}
                onClick={() => { }}
                icon={stageIcons[activeTask.state] ? <IconFlag size={18} /> : <FileText size={18} />}
                taskTypeLabel={t(TASK_TYPE_I18N_KEY[activeTask.taskType] ?? activeTask.taskType)}
              />
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      <ImportConfirmModal
        opened={importConfirm != null}
        newCount={importConfirm?.newCount ?? 0}
        updateCount={importConfirm?.updateCount ?? 0}
        deleteCount={importConfirm?.deleteCount ?? 0}
        entityKey="tasks"
        onCancel={() => setImportConfirm(null)}
        onConfirm={() => {
          const run = importConfirm?.run;
          setImportConfirm(null);
          void run?.();
        }}
      />

      {/* Modal para cadastro/edição ou importação */}
      <Modal
        opened={modalOpened}
        onClose={closeTaskModal}
        centered
        title={
          editingTask
            ? t('tasks.editTask')
            : t('tasks.addNewTask')
        }
        size="xl"
        styles={{ body: { padding: '0' } }}
      >
        <ScrollArea w="100%" h={modalScrollAreaHeight}>
              <div style={{ padding: '1rem' }}>
                <TextInput
                  label={t('tasks.form.title')}
                  placeholder={t('tasks.form.titlePlaceholder')}
                  value={taskForm.name}
                  onChange={(e) => settaskForm({ ...taskForm, name: e.target.value })}
                  mb="sm"
                  required
                />
                <Group grow mb="sm">
                  <Select
                    label={t('tasks.form.assignedTo')}
                    placeholder={t('tasks.form.selectAssignee')}
                    searchable
                    data={responsibleOptions}
                    value={taskForm.responsible_id}
                    onChange={(value) => {
                      const selected = responsibleOptions.find((opt) => opt.value === value);
                      settaskForm({
                        ...taskForm,
                        responsible_id: value || '',
                        responsible_name: selected?.label || '',
                      });
                    }}
                  />
                  {editingTask ? (
                    <Select
                      label={t('tasks.form.status')}
                      placeholder={t('tasks.form.selectStatus')}
                      data={stateOptions}
                      value={taskForm.stage_id}
                      onChange={(value) => {
                        const selectedStage = taskSettings?.stages.find((stage) => stage.id === value);
                        settaskForm({
                          ...taskForm,
                          stage_id: value || '',
                          state: selectedStage?.is_done
                            ? 'Done'
                            : selectedStage?.position === 0
                              ? 'To do'
                              : 'Doing',
                        });
                      }}
                      required
                    />
                  ) : (
                    <TextInput
                      label={t('tasks.form.status')}
                      value={taskSettings?.stages[0]?.name || t('tasks.state.toDo')}
                      disabled
                    />
                  )}
                </Group>

                {editingTask && (
                  <Group grow mb="sm">
                    <TextInput
                      label={t('tasks.form.createdBy')}
                      value={taskForm.created_by_name}
                      disabled
                    />
                    <TextInput
                      label={t('tasks.form.createdAt')}
                      value={formatAppDate(editingTask.created_at)}
                      disabled
                    />
                  </Group>
                )}

                <Group grow mb="sm">
                  <Select
                    label={t('tasks.form.opportunityOptional')}
                    placeholder={t('tasks.form.linkOpportunity')}
                    data={opportunitiesOptions}
                    value={selectedOpportunity}
                    onChange={(value) => {
                      setSelectedOpportunity(value || '');
                      const opp = opportunitiesOptions.find((o) => o.value === value);
                      setSelectedClient(
                        opp?.client_ids?.length === 1
                          ? clientsOptions.find((c) => c.value === opp.client_ids[0])?.value || ''
                          : '',
                      );
                      settaskForm({ ...taskForm, linked: opp?.label || '' });
                    }}
                    disabled={selectedClient !== '' && selectedOpportunity === ""}
                    clearable
                    searchable
                  />
                  <Select
                    label={t('tasks.form.clientOptional')}
                    placeholder={t('tasks.form.linkClient')}
                    data={clientsOptions}
                    value={selectedClient}
                    onChange={(value) => {
                      setSelectedClient(value || '');
                      setSelectedOpportunity('');
                      const contact = contactsOptions.find((c) => c.value === selectedContact);
                      if (contact && !contact.clients.includes(value as string)) {
                        setSelectedContact("");
                      }
                      const client = clientsOptions.find((c) => c.value === value);
                      settaskForm({ ...taskForm, linked: client?.label || '' });
                    }}
                    disabled={selectedOpportunity !== ''}
                    clearable
                    searchable
                  />
                </Group>

                <Select
                  label={t('tasks.form.contactOptional')}
                  data={filterContacts()}
                  value={selectedContact}
                  onChange={(value) => {
                    setSelectedContact(value || '');
                    settaskForm({ ...taskForm, contact_id: value || '' });
                  }}
                  clearable
                  searchable
                  mb="sm"
                />
                <Group grow mb="sm">
                  <Select
                    label={t('tasks.form.type')}
                    placeholder={t('tasks.form.selectTaskType')}
                    data={taskTypeOptions}
                    value={taskForm.taskType}
                    onChange={(value) =>
                      settaskForm({ ...taskForm, taskType: value || 'tarefa' })
                    }
                    searchable
                  />
                  <NumberInput
                    label={t('tasks.form.priority')}
                    placeholder={t('tasks.form.priorityHint')}
                    value={taskForm.priority}
                    onChange={(value) =>
                      settaskForm({ ...taskForm, priority: Number(value) || 0 })
                    }
                    min={0}
                    max={4}
                  />
                </Group>
                <Group grow mb="sm">
                  <Select
                    label={t('tasks.sprints.form.sprint')}
                    placeholder={t('tasks.sprints.form.noSprint')}
                    data={sprintOptions}
                    value={taskForm.sprint_id || ''}
                    onChange={(value) =>
                      settaskForm({ ...taskForm, sprint_id: value || null })
                    }
                    clearable
                    searchable
                    disabled={isReadOnlySprint}
                  />
                  <Select
                    label={t('tasks.sprints.form.storyPoints')}
                    placeholder="—"
                    data={STORY_POINT_OPTIONS}
                    value={taskForm.story_points != null ? String(taskForm.story_points) : ''}
                    onChange={(value) =>
                      settaskForm({
                        ...taskForm,
                        story_points: value ? Number.parseInt(value, 10) : null,
                      })
                    }
                    clearable
                  />
                  <NumberInput
                    label={t('tasks.sprints.form.estimatedTime')}
                    placeholder="—"
                    value={taskForm.estimated_minutes ?? ''}
                    onChange={(value) =>
                      settaskForm({
                        ...taskForm,
                        estimated_minutes: value === '' || value == null ? null : Number(value),
                      })
                    }
                    min={0}
                  />
                </Group>
                <DateInput
                  label={t('tasks.form.deadline')}
                  placeholder={t('tasks.form.selectDeadline')}
                  value={calendarDate(taskForm.dueDate)}
                  onChange={(date) => {
                    settaskForm({
                      ...taskForm,
                      dueDate: date ? date.toISOString() : '',
                    })
                  }}
                  mb="sm"
                  clearable
                />

                <MentionTextarea
                  label={t('tasks.form.description')}
                  placeholder={t('tasks.form.descriptionPlaceholder')}
                  value={taskForm.description}
                  onChange={(description) => settaskForm({ ...taskForm, description })}
                  users={mentionUsers}
                  minRows={4}
                  required
                  mb="sm"
                />

                {editingTask && (
                  <Stack spacing="xs" mb="sm">
                    <Text weight={600} size="sm">{t('tasks.comments.title')}</Text>
                    <ScrollArea
                      h="min(360px, 35vh)"
                      type="auto"
                      offsetScrollbars
                    >
                      <Stack spacing="xs">
                        {commentsLoading && (
                          <Text size="sm" color="dimmed">{t('tasks.comments.loading')}</Text>
                        )}
                        {!commentsLoading && taskComments.length === 0 && (
                          <Text size="sm" color="dimmed">{t('tasks.comments.empty')}</Text>
                        )}
                        {taskComments.map((comment) => (
                          <Paper key={comment.id} withBorder p="xs" radius="md">
                            <Group spacing="xs" mb={4} position="apart" noWrap>
                              <Group spacing="xs" noWrap style={{ minWidth: 0 }}>
                                {comment.user_image && (
                                  <Image
                                    src={parsePhotoUrl(comment.user_image)}
                                    alt={comment.user_name}
                                    width={24}
                                    height={24}
                                    radius="xl"
                                  />
                                )}
                                <Text size="xs" weight={600}>{comment.user_name}</Text>
                                <Text size="xs" color="dimmed">
                                  {formatAppDateTime(comment.created_at)}
                                </Text>
                              </Group>
                              {canDeleteComment(comment) && (
                                <ActionIcon
                                  color="red"
                                  variant="subtle"
                                  size="sm"
                                  title={t('tasks.comments.delete')}
                                  onClick={() => void deleteComment(comment.id)}
                                >
                                  <IconTrash size={14} />
                                </ActionIcon>
                              )}
                            </Group>
                            <Text size="sm">{formatMentionText(comment.body)}</Text>
                          </Paper>
                        ))}
                      </Stack>
                    </ScrollArea>
                    <Group align="flex-end" spacing="xs" noWrap>
                      <Box style={{ flex: 1 }}>
                        <MentionTextarea
                          placeholder={t('tasks.comments.placeholder')}
                          value={newComment}
                          onChange={setNewComment}
                          users={mentionUsers}
                          minRows={2}
                        />
                      </Box>
                      <ActionIcon
                        color="green"
                        variant="filled"
                        size="lg"
                        loading={commentSaving}
                        disabled={!newComment.trim()}
                        onClick={() => void submitComment()}
                        title={t('tasks.comments.send')}
                        style={{ marginBottom: 4 }}
                      >
                        <IconSend size={18} />
                      </ActionIcon>
                    </Group>
                  </Stack>
                )}

                <Stack spacing="xs" mb="sm">
                  <Text weight={600} size="sm">{t('tasks.form.attachments')}</Text>
                  <div
                    {...getAttachmentRootProps()}
                    style={{
                      border: `2px dashed ${isAttachmentDragActive ? theme.colors.green[6] : '#ccc'}`,
                      borderRadius: 8,
                      padding: 24,
                      textAlign: 'center',
                      cursor: 'pointer',
                      backgroundColor: isAttachmentDragActive ? 'rgba(48,113,91,0.05)' : '#fafafa',
                    }}
                  >
                    <input {...getAttachmentInputProps()} />
                    {isAttachmentDragActive ? (
                      <IconPhoto size={40} color={theme.colors.green[6]} />
                    ) : (
                      <IconFile size={40} color={theme.colors.gray[6]} />
                    )}
                    <Text size="sm" mt="xs">
                      {t('tasks.form.dragDropHint')}
                    </Text>
                    <Text size="xs" color="dimmed">
                      {t('tasks.form.dragDropSubhint')}
                    </Text>
                  </div>

                  {attachmentErrors.length > 0 && (
                    <Stack spacing={4}>
                      {attachmentErrors.map((err, idx) => (
                        <Text key={idx} color="red" size="sm">{err}</Text>
                      ))}
                    </Stack>
                  )}

                  {taskForm.files.length > 0 && (
                    <Table highlightOnHover withBorder striped>
                      <thead>
                        <tr>
                          <th>{t('tasks.form.fileName')}</th>
                          <th>{t('tasks.form.fileSize')}</th>
                          <th>{t('tasks.form.actions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {taskForm.files.map((att) => (
                          <tr key={att.id}>
                            <td>{att.file_name}</td>
                            <td>{(att.size / (1024 * 1024)).toFixed(2)} MB</td>
                            <td>
                              {getTaskAttachmentSource(att) ? (
                                <AttachmentActionButtons
                                  onPreview={() => {
                                    const source = getTaskAttachmentSource(att);
                                    if (source) openPreview(source);
                                  }}
                                  onDownload={() => void downloadTaskAttachment(att)}
                                  onRemove={() => removeAttachment(att.id)}
                                  downloadLoading={downloadingAttachmentId === att.id}
                                  showRemove
                                />
                              ) : (
                                <AttachmentActionButtons
                                  onPreview={() => {}}
                                  onDownload={() => {}}
                                  onRemove={() => removeAttachment(att.id)}
                                  showPreview={false}
                                  showDownload={false}
                                  showRemove
                                />
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Stack>
              </div>
            </ScrollArea>

            <Group position="right" mt="md" style={{ margin: '1rem' }}>
              {editingTask?.invoice_id && (
                <Button
                  variant="light"
                  onClick={() => {
                    closeTaskModal();
                    navigate(buildPageUrl('/invoices', { invoiceId: editingTask.invoice_id! }));
                  }}
                >
                  {t('tasks.viewInvoice')}
                </Button>
              )}
              {editingTask && (
                <Button
                  onClick={deleteTask}
                  sx={{
                    backgroundColor: 'rgba(163, 0, 0, 0.9)',
                    color: 'white',
                    '&:hover': { backgroundColor: 'rgba(121, 0, 0, 0.9)' },
                  }}
                >
                  {t('common.delete')}
                </Button>
              )}
              <Button
                loading={saving}
                onClick={editingTask ? updateTask : saveTask}
                disabled={!isFormValid() || saving}
              >
                {editingTask ? t('common.update') : t('common.save')}
              </Button>
            </Group>
        {errorMessage && (
          <Group position="center" mt="md">
            <Text color="red">{errorMessage || null}</Text>
          </Group>
        )}
      </Modal>

      <Modal
        opened={sprintModalOpened}
        onClose={() => setSprintModalOpened(false)}
        centered
        title={t('tasks.sprints.createSprint')}
      >
        <Stack spacing="sm">
          <TextInput
            label={t('tasks.sprints.name')}
            placeholder={t('tasks.sprints.namePlaceholder')}
            value={newSprintForm.name}
            onChange={(e) => setNewSprintForm({ ...newSprintForm, name: e.target.value })}
            required
          />
          <Textarea
            label={t('tasks.sprints.goal')}
            placeholder={t('tasks.sprints.goalPlaceholder')}
            value={newSprintForm.goal}
            onChange={(e) => setNewSprintForm({ ...newSprintForm, goal: e.target.value })}
          />
          <Group grow>
            <DateInput
              label={t('tasks.sprints.startDate')}
              value={calendarDate(newSprintForm.start_date)}
              onChange={(date) =>
                setNewSprintForm({
                  ...newSprintForm,
                  start_date: date ? date.toISOString() : '',
                })
              }
              required
            />
            <DateInput
              label={t('tasks.sprints.endDate')}
              value={calendarDate(newSprintForm.end_date)}
              onChange={(date) =>
                setNewSprintForm({
                  ...newSprintForm,
                  end_date: date ? date.toISOString() : '',
                })
              }
              required
            />
          </Group>
          <Select
            label={t('tasks.sprints.status')}
            data={[
              { value: 'planned', label: t('tasks.sprints.statusPlanned') },
              { value: 'active', label: t('tasks.sprints.statusActive') },
            ]}
            value={newSprintForm.status}
            onChange={(value) =>
              setNewSprintForm({
                ...newSprintForm,
                status: (value as SprintStatus) || 'planned',
              })
            }
          />
          <Group position="right">
            <Button variant="default" onClick={() => setSprintModalOpened(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              loading={savingSprint}
              onClick={createSprint}
              disabled={!newSprintForm.name || !newSprintForm.start_date || !newSprintForm.end_date}
            >
              {t('tasks.sprints.createSprint')}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={settingsModalOpened}
        onClose={() => setSettingsModalOpened(false)}
        centered
        size="lg"
        title={t('tasks.settings.title')}
      >
        {settingsDraft && (
          <Stack spacing="md">
            <Select
              label={t('tasks.settings.defaultSort')}
              data={sortOptions}
              value={settingsDraft.default_sort}
              onChange={(value) =>
                setSettingsDraft({
                  ...settingsDraft,
                  default_sort: (value as TaskSortField) || 'created_at',
                })
              }
            />
            <Divider label={t('tasks.settings.stagesTitle')} labelPosition="center" />
            {settingsDraft.stages.map((stage, index) => (
              <Group key={stage.id || `new-${index}`} align="flex-end" spacing="sm">
                <TextInput
                  label={t('tasks.settings.stageName', { number: index + 1 })}
                  value={stage.name}
                  onChange={(e) => {
                    const stages = [...settingsDraft.stages];
                    stages[index] = { ...stage, name: e.target.value };
                    setSettingsDraft({ ...settingsDraft, stages });
                  }}
                  style={{ flex: 1 }}
                />
                <Switch
                  label={t('tasks.settings.doneStage')}
                  checked={stage.is_done}
                  onChange={(event) => {
                    const stages = [...settingsDraft.stages];
                    stages[index] = { ...stage, is_done: event.currentTarget.checked };
                    setSettingsDraft({ ...settingsDraft, stages });
                  }}
                />
                <ActionIcon
                  color="red"
                  variant="light"
                  disabled={settingsDraft.stages.length <= 1}
                  onClick={() => {
                    const stages = settingsDraft.stages.filter((_, stageIndex) => stageIndex !== index);
                    setSettingsDraft({ ...settingsDraft, stages });
                  }}
                  title={t('tasks.settings.removeStage')}
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            ))}
            <Button
              variant="light"
              leftIcon={<IconPlus size={16} />}
              onClick={() =>
                setSettingsDraft({
                  ...settingsDraft,
                  stages: [
                    ...settingsDraft.stages,
                    {
                      id: '',
                      enterprise_id: settingsDraft.enterprise_id,
                      name: t('tasks.settings.newStageDefault'),
                      position: settingsDraft.stages.length,
                      is_done: false,
                    },
                  ],
                })
              }
            >
              {t('tasks.settings.addStage')}
            </Button>
            <Group position="right">
              <Button variant="default" onClick={() => setSettingsModalOpened(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                loading={settingsSaving}
                onClick={() => void saveTaskSettings()}
                disabled={settingsDraft.stages.some((stage) => !stage.name.trim())}
              >
                {t('common.save')}
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
      {previewModal}
    </>
  );
}
